import { useParams, Link } from "react-router-dom";
import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const SITE_URL = "http://cqxbygc.cn";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 md:pt-24 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <span className="w-16 h-16 flex items-center justify-center mx-auto text-foreground-300">
              <i className="ri-file-close-line text-4xl" />
            </span>
            <h1 className="mt-4 text-xl font-bold text-foreground-950">文章未找到</h1>
            <p className="mt-2 text-sm text-foreground-500">该文章可能已被删除或链接有误</p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center px-5 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 4);

  const formatCount = (n: number) => {
    if (n >= 10000) return `${(n / 10000).toFixed(1)}万`;
    return n.toLocaleString();
  };

  const category = categories.find((c) => c.slug === article.categorySlug);

  return (
    <>
      <title>{`${article.title} - 91吃瓜`}</title>
      <meta name="description" content={article.excerpt} />
      <meta property="og:title" content={article.title} />
      <meta property="og:description" content={article.excerpt} />
      <meta property="og:type" content="article" />
      <meta property="article:published_time" content={article.publishDate} />
      <meta property="article:section" content={article.category} />
      <link rel="canonical" href={`/article/${slug}`} />

      {/* Article Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: article.title,
          description: article.excerpt,
          image: article.imageUrl,
          datePublished: article.publishDate,
          dateModified: article.publishDate,
          author: {
            "@type": "Person",
            name: article.author,
          },
          publisher: {
            "@type": "Organization",
            name: "91吃瓜社区",
            url: SITE_URL,
            logo: {
              "@type": "ImageObject",
              url: `${SITE_URL}/vite.svg`,
            },
          },
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": `${SITE_URL}/article/${slug}`,
          },
          articleSection: article.category,
          keywords: article.tags.join(","),
        })}
      </script>

      {/* NewsArticle Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "NewsArticle",
          headline: article.title,
          description: article.excerpt,
          image: article.imageUrl,
          datePublished: article.publishDate,
          dateModified: article.publishDate,
          author: {
            "@type": "Organization",
            name: article.author,
          },
          publisher: {
            "@type": "Organization",
            name: "91吃瓜社区",
            logo: {
              "@type": "ImageObject",
              url: `${SITE_URL}/vite.svg`,
            },
          },
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": `${SITE_URL}/article/${slug}`,
          },
        })}
      </script>

      {/* WebPage Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: `${article.title} - 91吃瓜`,
          description: article.excerpt,
          url: `${SITE_URL}/article/${slug}`,
          isPartOf: {
            "@type": "WebSite",
            name: "91吃瓜社区",
            url: SITE_URL,
          },
          mainEntity: {
            "@type": "NewsArticle",
            headline: article.title,
          },
        })}
      </script>

      {/* Breadcrumb Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "首页",
              item: SITE_URL,
            },
            {
              "@type": "ListItem",
              position: 2,
              name: article.category,
              item: `${SITE_URL}/category/${article.categorySlug}`,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: article.title,
              item: `${SITE_URL}/article/${slug}`,
            },
          ],
        })}
      </script>

      <Navbar />

      <main className="pt-16 md:pt-20">
        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-6 lg:px-8 py-3 md:py-4">
          <nav aria-label="面包屑导航">
            <ol className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
              <li>
                <Link to="/" className="hover:text-primary-500 transition-colors">
                  首页
                </Link>
              </li>
              <li>
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line" />
                </span>
              </li>
              <li>
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="hover:text-primary-500 transition-colors"
                >
                  {article.category}
                </Link>
              </li>
              <li>
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line" />
                </span>
              </li>
              <li className="text-foreground-800 font-medium line-clamp-1 max-w-[200px] md:max-w-xs">
                {article.title}
              </li>
            </ol>
          </nav>
        </div>

        <div className="w-full px-4 md:px-6 lg:px-8 py-4 md:py-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            {/* Main Content */}
            <article className="lg:col-span-2">
              <div className="bg-background-50 rounded-xl border border-background-200/60 overflow-hidden">
                {/* Hero Image */}
                <div className="aspect-[16/9] overflow-hidden">
                  <img
                    src={article.imageUrl}
                    alt={article.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>

                <div className="p-5 md:p-8">
                  {/* Meta */}
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                      {article.category}
                    </span>
                    {article.isHot && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">
                        <i className="ri-fire-line" />
                        热门
                      </span>
                    )}
                    <span className="text-xs text-foreground-500">{article.publishDate}</span>
                  </div>

                  {/* Title */}
                  <h1
                    className="text-2xl md:text-3xl font-bold text-foreground-950 leading-snug"
                    style={{ fontFamily: "var(--font-heading)" }}
                  >
                    {article.title}
                  </h1>

                  {/* Author & Stats */}
                  <div className="mt-4 flex items-center gap-4 text-sm text-foreground-500 border-b border-background-200/60 pb-4">
                    <span className="flex items-center gap-1.5">
                      <span className="w-6 h-6 flex items-center justify-center rounded-full bg-secondary-200 text-secondary-700 text-xs">
                        <i className="ri-user-line" />
                      </span>
                      {article.author}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line" />
                      {formatCount(article.readCount)}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-message-3-line" />
                      {article.commentCount}
                    </span>
                  </div>

                  {/* Content */}
                  <div className="mt-6 prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed">
                    <p className="text-base md:text-lg font-medium text-foreground-700 mb-4">
                      {article.excerpt}
                    </p>
                    <p className="mb-4">
                      据知情人士透露，此事近日在网络上持续发酵，引发了大量网友的关注和讨论。多方消息源交叉印证，事件的核心细节逐渐浮出水面。
                    </p>
                    <p className="mb-4">
                      有业内人士表示，类似事件在行业内并非孤例，但此次事件之所以引发如此广泛的关注，主要与其当事人的公众形象形成了鲜明对比。不少网友在社交媒体上表达了自己的看法和态度。
                    </p>
                    <p className="mb-4">
                      截至目前，相关当事人尚未对此事做出正式回应。但有消息称，其团队正在紧急商讨应对策略，预计将在未来24小时内发布正式声明。
                    </p>
                    <div className="my-6 p-4 bg-background-100 rounded-lg border-l-4 border-primary-400">
                      <p className="text-sm text-foreground-700 italic">
                        "这件事的发展确实出乎很多人的意料，但我们也要理性看待，等待更多信息的披露。"
                      </p>
                      <p className="mt-2 text-xs text-foreground-500">—— 网友热评</p>
                    </div>
                    <p className="mb-4">
                      值得注意的是，该事件已经在多个社交平台登上了热搜榜前列，相关话题阅读量已突破千万。业内人士分析，此类事件的传播速度和影响范围在近年来呈现明显上升趋势。
                    </p>
                    <p className="mb-4">
                      91吃瓜社区将持续关注此事的最新进展，并在第一时间为各位吃瓜群众带来最新动态。请大家理性吃瓜，不传谣不信谣。
                    </p>
                  </div>

                  {/* Tags */}
                  <div className="mt-6 pt-4 border-t border-background-200/60">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm text-foreground-500">标签：</span>
                      {article.tags.map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </article>

            {/* Sidebar */}
            <aside className="lg:col-span-1 space-y-6">
              {/* Category Info */}
              <div className="bg-background-50 rounded-xl border border-background-200/60 p-4 md:p-5">
                <h3 className="text-sm font-semibold text-foreground-950 mb-3">所属分类</h3>
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="flex items-center gap-3 p-3 rounded-lg bg-background-100 hover:bg-primary-50 transition-colors group"
                >
                  <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 group-hover:bg-primary-200 transition-colors">
                    <i className={`${category?.icon || "ri-folder-line"} text-base`} />
                  </span>
                  <div>
                    <p className="text-sm font-medium text-foreground-950 group-hover:text-primary-600 transition-colors">
                      {article.category}
                    </p>
                    <p className="text-xs text-foreground-500">
                      {category?.articleCount || 0} 篇文章
                    </p>
                  </div>
                </Link>
              </div>

              {/* Related Articles */}
              <div className="bg-background-50 rounded-xl border border-background-200/60 p-4 md:p-5">
                <h3 className="text-sm font-semibold text-foreground-950 mb-3">相关推荐</h3>
                <div className="space-y-1">
                  {relatedArticles.length > 0 ? (
                    relatedArticles.map((a) => (
                      <Link
                        key={a.id}
                        to={`/article/${a.slug}`}
                        className="group flex items-start gap-2 py-2 px-2 rounded-lg hover:bg-background-100 transition-colors"
                      >
                        <span className="w-16 h-12 shrink-0 rounded-md overflow-hidden">
                          <img
                            src={a.imageUrl}
                            alt={a.title}
                            className="w-full h-full object-cover object-top"
                            loading="lazy"
                          />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
                            {a.title}
                          </p>
                          <p className="mt-0.5 text-xs text-foreground-500">
                            {formatCount(a.readCount)} 阅读
                          </p>
                        </div>
                      </Link>
                    ))
                  ) : (
                    <p className="text-sm text-foreground-500">暂无相关文章</p>
                  )}
                </div>
              </div>

              {/* Hot Articles */}
              <div className="bg-background-50 rounded-xl border border-background-200/60 p-4 md:p-5">
                <h3 className="text-sm font-semibold text-foreground-950 mb-3">热门文章</h3>
                <div className="space-y-2">
                  {articles
                    .filter((a) => a.isHot)
                    .slice(0, 5)
                    .map((a, i) => (
                      <Link
                        key={a.id}
                        to={`/article/${a.slug}`}
                        className="group flex items-start gap-2 py-1.5"
                      >
                        <span
                          className={`shrink-0 w-5 h-5 flex items-center justify-center rounded text-xs font-bold ${
                            i < 3
                              ? "bg-primary-500 text-white"
                              : "bg-background-200 text-foreground-500"
                          }`}
                        >
                          {i + 1}
                        </span>
                        <p className="text-xs text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
                          {a.title}
                        </p>
                      </Link>
                    ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}
